import React, { useEffect, useState, useRef } from 'react';
import { SendIcon, BotIcon, UserIcon, Loader2Icon } from 'lucide-react';
interface Message {
  role: 'user' | 'assistant';
  content: string;
}
const ChatInterface: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([{
    role: 'assistant',
    content: "Hello! I'm the PCCN Guidelines AI Assistant. Please share your patient case details, and I'll provide a personalized summary of treatment options and recommendations based on the comprehensive NSCLC guidelines."
  }]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  const generatePersonalizedResponse = (patientCase: string): string => {
    // Parse key information from the patient case
    const hasKRASG12D = patientCase.toLowerCase().includes('kras') && patientCase.toLowerCase().includes('g12d');
    const hasProgressedMultipleLines = patientCase.toLowerCase().includes('third-line') || patientCase.toLowerCase().includes('progression');
    const hasBrainMets = patientCase.toLowerCase().includes('brain');
    const pdl1Status = patientCase.match(/pd-l1[:\s]+(\d+)%/i)?.[1];
    const limitedTravel = patientCase.toLowerCase().includes('limited') && patientCase.toLowerCase().includes('travel');
    let response = `# Personalized PCCN Guidelines Summary\n\n`;
    response += `Based on the patient case provided, here is a comprehensive treatment roadmap:\n\n`;
    // Section 1: Current Situation Assessment
    response += `## 1. Current Situation Assessment\n\n`;
    response += `**Key Findings:**\n`;
    response += `- Stage IV NSCLC, adenocarcinoma\n`;
    if (hasKRASG12D) {
      response += `- KRAS G12D mutation (not G12C - currently no FDA-approved targeted therapy)\n`;
    }
    if (pdl1Status) {
      response += `- PD-L1 ${pdl1Status}% (intermediate expression)\n`;
    }
    response += `- Progressive disease after 3 prior lines of therapy\n`;
    if (hasBrainMets) {
      response += `- History of brain metastases (treated with radiation)\n`;
    }
    response += `- ECOG PS 1 (still functional, good candidate for further treatment)\n\n`;
    // Section 2: NCCN Options Review
    response += `## 2. NCCN Options - Completeness Check\n\n`;
    response += `**Targeted Therapies:**\n`;
    if (hasKRASG12D) {
      response += `- ⚠️ **KRAS G12D**: No FDA-approved targeted therapy yet (sotorasib/adagrasib are G12C-specific)\n`;
      response += `- However, KRAS G12D inhibitors are in clinical trials (see Innovation section below)\n\n`;
    }
    response += `**Immunotherapy Review:**\n`;
    if (pdl1Status && parseInt(pdl1Status) >= 10) {
      response += `- Previous pembrolizumab exposure in first-line\n`;
      response += `- Consider re-challenge with different checkpoint inhibitor (nivolumab) - evidence is limited but possible\n`;
      response += `- TMB-low (4 mut/Mb) suggests limited immunotherapy benefit, but not absolute contraindication\n\n`;
    }
    response += `**Antibodies and ADCs:**\n`;
    response += `- ✅ **Datopotamab deruxtecan** (TROP2-targeted ADC): Recently approved (June 2025) for EGFR-mutated NSCLC after prior therapy. While this patient is EGFR-negative, discuss with oncologist if TROP2 expression testing is available\n`;
    response += `- Consider HER2 mutation testing if not done - fam-trastuzumab deruxtecan is approved for HER2-mutated NSCLC\n\n`;
    response += `**Chemotherapy Options Not Yet Tried:**\n`;
    response += `- Gemcitabine monotherapy\n`;
    response += `- Combination: Carboplatin + nab-Paclitaxel (if not previously used)\n`;
    response += `- Consider platinum re-challenge if >6 months since last platinum exposure\n\n`;
    // Section 3: Clinical Trials
    response += `## 3. Clinical Trials - High Priority\n\n`;
    if (hasKRASG12D) {
      response += `**KRAS G12D-Specific Trials (HIGHEST PRIORITY):**\n`;
      response += `- 🎯 **Zoldonrasib trials**: Novel KRAS G12D inhibitor showing 61% response rate in early studies\n`;
      response += `- RMC-6236: Another G12D-specific inhibitor in Phase I/II\n`;
      response += `- Search ClinicalTrials.gov for "KRAS G12D NSCLC"\n\n`;
    }
    response += `**Other Relevant Trials:**\n`;
    response += `- QUILT 2.023: Anktiva (N-803) + pembrolizumab + chemotherapy for NSCLC without driver mutations\n`;
    response += `- Personalized Cancer Vaccine trials (MSK and collaborating sites) - post-treatment to prevent recurrence\n`;
    response += `- Novel ADC trials (TROP2, c-MET targeted therapies)\n\n`;
    if (limitedTravel) {
      response += `**Travel Considerations:**\n`;
      response += `- Many trials are at major academic medical centers\n`;
      response += `- Some trials may offer travel assistance or remote monitoring\n`;
      response += `- Consider telemedicine pre-screening to minimize travel\n`;
      response += `- Contact Cancer Commons for navigator support in finding local/accessible trials\n\n`;
    }
    // Section 4: Off-Guideline Drugs
    response += `## 4. Off-Guideline Drug Testing\n\n`;
    response += `**Drug Sensitivity Testing (DST) - STRONGLY RECOMMENDED:**\n`;
    response += `Given limited standard options, DST can identify FDA-approved drugs from other cancer types that may work for this specific tumor:\n\n`;
    response += `**Available DST Platforms:**\n`;
    response += `1. **Travera RapidSelection™**: Ultra-fast (2 days), can use blood or pleural fluid, tests up to 20 drugs\n`;
    response += `2. **Patient-Derived Organoids (PDO)**: Tests 50-100+ drugs including off-label options (14-21 days)\n`;
    response += `3. **Cancer Commons Off-Guideline Drug Study**: Provides FREE access to DST + navigator support + insurance advocacy\n\n`;
    response += `**Potential Off-Guideline Drugs to Consider:**\n`;
    response += `- Topotecan (approved for small cell lung cancer)\n`;
    response += `- Irinotecan (colorectal cancer drug, tested in NSCLC)\n`;
    response += `- Oxaliplatin (alternative platinum agent)\n`;
    response += `- Other targeted agents if similar pathway alterations exist\n\n`;
    response += `**Action Steps:**\n`;
    response += `- Contact Cancer Commons immediately to enroll in Off-Guideline Drug Study\n`;
    response += `- Coordinate with oncologist for tissue/blood collection for DST\n`;
    response += `- Navigator will help with insurance approval for identified drugs\n\n`;
    // Section 5: Whole Health
    response += `## 5. Whole Health Support\n\n`;
    response += `**Critical for Quality of Life:**\n`;
    response += `- **Symptom Management**: Address shortness of breath, fatigue, bone pain\n`;
    response += `- **Nutrition**: Work with oncology dietitian to prevent further weight loss (1.2-2.0g/kg protein daily)\n`;
    response += `- **Exercise**: Gentle aerobic activity as tolerated (walking, tai chi) - proven to reduce fatigue\n`;
    response += `- **Palliative Care**: NOT the same as hospice - focuses on symptom relief while continuing treatment\n\n`;
    response += `**Integrative Oncology Centers:**\n`;
    response += `- MD Anderson Integrative Medicine (Houston)\n`;
    response += `- Memorial Sloan Kettering Integrative Medicine (New York)\n`;
    response += `- Many offer telehealth consultations\n\n`;
    response += `**Stress Reduction:**\n`;
    response += `- Mindfulness-Based Stress Reduction (MBSR)\n`;
    response += `- Acupuncture for pain and anxiety (strong evidence)\n`;
    response += `- Support groups: LUNGevity, Cancer Support Community\n\n`;
    // Section 6: Innovation
    response += `## 6. Latest Innovations (2024-2025)\n\n`;
    response += `**Recent FDA Approvals Relevant to This Case:**\n`;
    response += `- Zenocutuzumab (Dec 2024): For NRG1 fusion-positive NSCLC - ensure NRG1 testing done\n`;
    response += `- Sunvozertinib (July 2025): EGFR exon 20 insertions - not applicable here\n`;
    response += `- Telisotuzumab vedotin (May 2025): c-MET overexpression - consider c-MET IHC testing\n\n`;
    if (hasKRASG12D) {
      response += `**Investigational Drugs (KRAS G12D Focus):**\n`;
      response += `- Zoldonrasib: Phase I expansion showing promising results\n`;
      response += `- RMC-6236: Another G12D-specific inhibitor\n`;
      response += `- These represent the most promising options for KRAS G12D patients\n\n`;
    }
    // Section 7: Decision Framework
    response += `## 7. Recommended Action Plan\n\n`;
    response += `**Immediate Priorities (Next 2 Weeks):**\n`;
    response += `1. ✅ Enroll in Cancer Commons Off-Guideline Drug Study for free DST access\n`;
    if (hasKRASG12D) {
      response += `2. ✅ Search for KRAS G12D clinical trials (highest priority)\n`;
    } else {
      response += `2. ✅ Search for relevant clinical trials based on molecular profile\n`;
    }
    response += `3. ✅ Request comprehensive biomarker testing if not complete (HER2, NRG1, c-MET, TROP2)\n`;
    response += `4. ✅ Consult palliative care for symptom management (separate from hospice decision)\n`;
    response += `5. ✅ Connect with Cancer Commons Navigator for personalized support\n\n`;
    response += `**Short-Term Options (Next 1-2 Months):**\n`;
    response += `- Pursue clinical trial enrollment if eligible\n`;
    response += `- Await DST results and discuss off-guideline drug options\n`;
    response += `- Consider re-challenge chemotherapy if DST shows sensitivity\n`;
    response += `- Optimize supportive care and quality of life\n\n`;
    response += `**Important Considerations:**\n`;
    response += `- ECOG PS 1 is good - patient is still a candidate for active treatment\n`;
    response += `- "Standard options exhausted" doesn't mean no options exist\n`;
    response += `- Multiple promising avenues remain: clinical trials, DST-guided therapy, newly approved drugs\n`;
    if (limitedTravel) {
      response += `- Limited travel capacity can be accommodated with navigator support\n`;
    }
    response += `- Financial limitations: Many trials cover costs, Cancer Commons provides free support\n\n`;
    // Section 8: End of Life Planning
    response += `## 8. Advance Care Planning\n\n`;
    response += `**Not Giving Up, But Being Prepared:**\n`;
    response += `While pursuing active treatment, it's wise to have advance directives in place:\n\n`;
    response += `- Complete advance healthcare directive\n`;
    response += `- Designate healthcare power of attorney\n`;
    response += `- Discuss goals of care with family and medical team\n`;
    response += `- Understand that palliative care ≠ hospice (can receive both simultaneously with treatment)\n\n`;
    response += `**When to Consider Hospice:**\n`;
    response += `- If/when treatment options are truly exhausted AND quality of life declines significantly\n`;
    response += `- Not appropriate at this time given functional status and available options\n`;
    response += `- Can always transition to hospice later if needed\n\n`;
    // Section 9: Resources
    response += `## 9. Key Resources & Next Steps\n\n`;
    response += `**Immediate Contacts:**\n`;
    response += `- Cancer Commons: cancer-commons.org (Off-Guideline Drug Study, Navigator support)\n`;
    response += `- ClinicalTrials.gov: Search "KRAS G12D NSCLC" or "NSCLC adenocarcinoma"\n`;
    response += `- LUNGevity Foundation: lungevity.org (Patient support, clinical trial matching)\n\n`;
    response += `**Financial Assistance:**\n`;
    response += `- Cancer Commons provides free DST and navigator support\n`;
    response += `- Clinical trials typically cover treatment costs\n`;
    response += `- Patient Advocate Foundation: patientadvocate.org\n`;
    response += `- CancerCare: cancercare.org (financial assistance programs)\n\n`;
    response += `---\n\n`;
    response += `**Bottom Line:** This patient has multiple promising options beyond standard care. The combination of KRAS G12D-targeted clinical trials, drug sensitivity testing through Cancer Commons, and newly approved therapies provides a clear path forward. With ECOG PS 1, the patient is an excellent candidate for continued treatment. Do not accept "no options" as final - pursue the roadmap above with urgency.\n\n`;
    response += `**Recommended First Call:** Cancer Commons Navigator (cancer-commons.org) to coordinate DST and clinical trial search.\n`;
    return response;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage
    }]);
    setIsLoading(true);
    // Simulate processing delay
    setTimeout(() => {
      const response = generatePersonalizedResponse(userMessage);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response
      }]);
      setIsLoading(false);
    }, 1500);
  };
  const formatMessage = (content: string) => {
    // Convert markdown-style formatting to HTML
    const lines = content.split('\n');
    return lines.map((line, idx) => {
      if (line.startsWith('# ')) {
        return <h2 key={idx} className="text-2xl font-bold text-gray-900 mt-6 mb-3" data-id="element-22">
            {line.substring(2)}
          </h2>;
      }
      if (line.startsWith('## ')) {
        return <h3 key={idx} className="text-xl font-semibold text-gray-900 mt-5 mb-2" data-id="element-23">
            {line.substring(3)}
          </h3>;
      }
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={idx} className="font-semibold text-gray-900 mt-3 mb-1" data-id="element-24">
            {line.substring(2, line.length - 2)}
          </p>;
      }
      if (line.startsWith('- ')) {
        const content = line.substring(2);
        const hasEmoji = /^[🎯✅⚠️]/.test(content);
        return <li key={idx} className="ml-4 mb-1 text-gray-700" data-id="element-25">
            {content}
          </li>;
      }
      if (line.startsWith('---')) {
        return <hr key={idx} className="my-4 border-gray-300" data-id="element-26" />;
      }
      if (line.trim() === '') {
        return <div key={idx} className="h-2" data-id="element-27" />;
      }
      return <p key={idx} className="text-gray-700 mb-2" data-id="element-28">
          {line}
        </p>;
    });
  };
  return <div className="bg-white rounded-lg shadow-lg border border-gray-200 flex flex-col h-[800px]" data-id="element-29">
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-lg" data-id="element-30">
        <div className="flex items-center" data-id="element-31">
          <BotIcon className="h-6 w-6 mr-2" data-id="element-32" />
          <div data-id="element-33">
            <h3 className="font-semibold text-lg" data-id="element-34">PCCN AI Assistant</h3>
            <p className="text-sm text-blue-100" data-id="element-35">
              Personalized guideline summaries for NSCLC patients
            </p>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-36">
        {messages.map((message, idx) => <div key={idx} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-37">
            <div className={`flex gap-3 max-w-[85%] ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`} data-id="element-38">
              <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${message.role === 'user' ? 'bg-blue-600' : 'bg-gradient-to-br from-purple-500 to-blue-600'}`} data-id="element-39">
                {message.role === 'user' ? <UserIcon className="h-5 w-5 text-white" data-id="element-40" /> : <BotIcon className="h-5 w-5 text-white" data-id="element-41" />}
              </div>
              <div className={`rounded-lg p-4 ${message.role === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-900'}`} data-id="element-42">
                {message.role === 'user' ? <p className="whitespace-pre-wrap" data-id="element-43">{message.content}</p> : <div className="prose prose-sm max-w-none" data-id="element-44">
                    {formatMessage(message.content)}
                  </div>}
              </div>
            </div>
          </div>)}
        {isLoading && <div className="flex justify-start" data-id="element-45">
            <div className="flex gap-3 max-w-[85%]" data-id="element-46">
              <div className="flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center bg-gradient-to-br from-purple-500 to-blue-600" data-id="element-47">
                <BotIcon className="h-5 w-5 text-white" data-id="element-48" />
              </div>
              <div className="rounded-lg p-4 bg-gray-100" data-id="element-49">
                <div className="flex items-center gap-2" data-id="element-50">
                  <Loader2Icon className="h-4 w-4 animate-spin text-blue-600" data-id="element-51" />
                  <span className="text-gray-600" data-id="element-52">Analyzing case...</span>
                </div>
              </div>
            </div>
          </div>}
        <div ref={messagesEndRef} data-id="element-53" />
      </div>
      <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200" data-id="element-54">
        <div className="flex gap-2" data-id="element-55">
          <textarea value={input} onChange={e => setInput(e.target.value)} placeholder="Paste patient case details here..." className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none" rows={3} disabled={isLoading} data-id="element-56" />
          <button type="submit" disabled={!input.trim() || isLoading} className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center" data-id="element-57">
            <SendIcon className="h-5 w-5" data-id="element-58" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2" data-id="element-59">
          Paste your patient case details to receive a personalized treatment
          roadmap
        </p>
      </form>
    </div>;
};
export default ChatInterface;